import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare, BookOpen, Zap, Lock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api";

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      await login(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (error) {
      const errorMessage =
        error instanceof ApiError
          ? error.message
          : "E-mail ou senha incorretos. Tente novamente.";
      toast({
        title: "Erro no login",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] shrink-0 bg-primary/5 border-r border-border p-10">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground text-base font-bold leading-none">V</span>
          </div>
          <div>
            <p className="text-sm font-bold text-foreground leading-none">Venda Todo Santo Dia</p>
            <p className="text-xs text-muted-foreground leading-none mt-0.5">Central de Suporte</p>
          </div>
        </div>

        {/* Feature list */}
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground leading-snug">
              Painel de administração
              <br />
              <span className="text-primary">do suporte IA</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Gerencie conversas, base de conhecimento e integrações da sua central de ajuda com IA.
            </p>
          </div>

          <ul className="space-y-4">
            {[
              { icon: MessageSquare, label: "Conversas em tempo real", desc: "Visualize e gerencie todas as sessões do chat" },
              { icon: BookOpen, label: "Base de Conhecimento", desc: "Artigos, PDFs e URLs alimentam a IA automaticamente" },
              { icon: Zap, label: "Pipelines de integração", desc: "Conecte ao Oktadesk e outros sistemas" },
            ].map(({ icon: Icon, label, desc }) => (
              <li key={label} className="flex items-start gap-3">
                <div className="mt-0.5 h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="h-3.5 w-3.5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <p className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">
            ← Voltar ao chat público
          </Link>
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground text-sm font-bold leading-none">V</span>
            </div>
            <div>
              <p className="text-sm font-bold leading-none">Venda Todo Santo Dia</p>
              <p className="text-xs text-muted-foreground leading-none mt-0.5">Central de Suporte</p>
            </div>
          </div>

          <div>
            <h1 className="text-xl font-semibold text-foreground">Acesso administrativo</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Entre com suas credenciais de administrador.
            </p>
          </div>

          <Card className="p-6 shadow-sm">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="admin@email.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Entrando...</>
                ) : (
                  <><Lock className="h-4 w-4 mr-2" />Entrar</>
                )}
              </Button>
            </form>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            Acesso restrito a administradores autorizados.
          </p>

          {/* Mobile back link */}
          <div className="lg:hidden text-center">
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              ← Voltar ao chat público
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
